import { makeAutoObservable } from "mobx";

import { ArticlesOfAssociation, DialogState } from "../types/ArticlesOfAssociation";
import { ApiService } from "../api";
import UserStore from "./UserStore";

import { TMessage } from "../i18n/tt";
import { showSuccess } from "../events/eventsDomain";

export default class ArticleStore {
  articlesOfAssociation: ArticlesOfAssociation[] = [];
  articlesError?: TMessage;
  isLoading = false;
  isDownloadingPDF = false;

  dialogState: DialogState = {
    state: "close",
  };

  apiService: ApiService;
  userStore: UserStore;

  constructor(apiService: ApiService, userStore: UserStore) {
    this.apiService = apiService;
    this.userStore = userStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *fetchArticlesOfAssociation() {
    try {
      this.setLoading(true);
      const articles: ArticlesOfAssociation[] = yield this.apiService.articlesOfAssociation(
        this.userStore.orgNumber,
      );
      this.setArticles(articles);
    } catch (e) {
      this.setArticlesError({ key: "articlesOfAssociation.fetchError" });
    } finally {
      this.setLoading(false);
    }
  }

  *editArticles(articles: Omit<ArticlesOfAssociation, "organizationnumber">) {
    try {
      this.setLoading(true);
      yield this.apiService.updateArticlesOfAssociation(
        { ...articles, organizationnumber: this.userStore.orgNumber },
        this.userStore.orgNumber,
      );
      yield this.userStore.updateCompanyInfo();
      yield this.fetchArticlesOfAssociation();
    } catch (error) {
      this.setArticlesError({ key: "articlesOfAssociation.saveError" });
    } finally {
      this.setLoading(false);
    }
  }

  *updateApprovedDate(date: string) {
    try {
      this.setLoading(true);
      yield this.apiService.updateArticlesOfAssociationDate(date, this.userStore.orgNumber);
      yield this.userStore.updateCompanyInfo();
      yield this.fetchArticlesOfAssociation();

      this.userStore.dispatchEvent([
        showSuccess({ key: "articlesOfAssociation.approvalDate.success" }),
      ]);
    } catch (error) {
      this.setArticlesError({ key: "articlesOfAssociation.saveError" });
    }
  }

  *downloadAsPDF() {
    try {
      this.isDownloadingPDF = true;
      yield this.apiService.downloadArticlesOfAssociationAsPDF(this.userStore.orgNumber);
    } catch (error) {
      this.setArticlesError({ key: "articlesOfAssociation.saveError" });
    } finally {
      this.isDownloadingPDF = false;
    }
  }

  setArticles(articles: ArticlesOfAssociation[]) {
    this.articlesOfAssociation = articles.sort(
      (a, b) => new Date(`${b.time_stamp}`).getTime() - new Date(`${a.time_stamp}`).getTime(),
    );
  }

  setArticlesError(error: TMessage) {
    this.articlesError = error;
  }

  setDialogState(dialogState: DialogState) {
    this.dialogState = dialogState;
  }

  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }
}
